import styled from "styled-components";
import {
  TableCell,
  TABLE_SIZE_THEMES,
  TABLE_SIZES,
  TableRow,
  TableHeaderGroup,
} from "@adsk/alloy-react-table";
import type { TableSize } from "@adsk/alloy-react-table/es/types";
import type { Column } from "@tanstack/react-table";
import Theme from "@adsk/alloy-react-theme";

const hexToRgba = (hex: string, alpha: number): string => {
  const cleanHex = hex.replace("#", "");
  const r = parseInt(cleanHex.slice(0, 2), 16);
  const g = parseInt(cleanHex.slice(2, 4), 16);
  const b = parseInt(cleanHex.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const rgbaToRgb = (rgba: string): string => {
  const match = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
  if (!match) return rgba;

  const [, r, g, b, a = "1"] = match;
  const alpha = parseFloat(a);

  if (alpha === 1) {
    return `rgb(${r}, ${g}, ${b})`;
  }

  const rBlended = Math.round((1 - alpha) * 255 + alpha * parseInt(r));
  const gBlended = Math.round((1 - alpha) * 255 + alpha * parseInt(g));
  const bBlended = Math.round((1 - alpha) * 255 + alpha * parseInt(b));

  return `rgb(${rBlended}, ${gBlended}, ${bBlended})`;
};

export const STATIC_CELL_STYLES: React.CSSProperties = {
  margin: 0,
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  outline: "none",
  boxShadow: "none",
  minWidth: 0,
  fontWeight: Theme.typography.labelMedium.fontWeight,
  fontSize: Theme.typography.labelMedium.fontSize,
  lineHeight: Theme.typography.labelMedium.lineHeight,
  fontFamily: Theme.typography.labelMedium.fontFamily,
};

export const CellContentWrapper = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  width: 100%;
`;

export const CheckboxContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
`;

export const getTableCellStyle = (
  tableSize: TableSize
): React.CSSProperties => {
  const theme =
    TABLE_SIZE_THEMES[tableSize] || TABLE_SIZE_THEMES[TABLE_SIZES.MEDIUM];
  const padding = theme.cellPadding;
  const height = theme.rowHeight;

  return {
    ...STATIC_CELL_STYLES,
    padding,
    height,
    minHeight: height,
  };
};

export const TableContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

export const ScrollableTableWrapper = styled.div`
  flex: 1;
  overflow: auto;
  position: relative;
  isolation: isolate;
  border: none;
`;

export const StyledTable = styled.table`
  width: 100%;
`;

export const StickyTableHead = styled.thead`
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: white;
`;

export const StyledTableHeaderGroup = styled(TableHeaderGroup)`
  display: flex;
`;

export const StickyTableCell = styled(TableCell)`
  position: sticky;
  padding: 0;
  ${(props: { height?: string | number; minHeight?: string | number }) => `
    z-index: 1;
    outline: none;
    display: flex;
    align-items: center;
    height: ${
      typeof props.height === "number" ? `${props.height}px` : props.height
    };
    min-height: ${
      typeof props.minHeight === "number"
        ? `${props.minHeight}px`
        : props.minHeight
    };
  `}
`;

export const StyledTableConfigurationMenu = styled.div`
  width: 240px;
  max-height: 400px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export const DragNDrop = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

export const IssuesTableWrapper = styled.div`
  width: 100%;
  height: 100%;
`;

export const getTableHeaderStyle = <TData = unknown>(
  isActionsColumn: boolean,
  isSelectColumn: boolean,
  baseStyle: React.CSSProperties = {},
  column?: Column<TData, unknown>
): React.CSSProperties => {
  const isStickyColumn = isActionsColumn || isSelectColumn;
  const columnWidth = column?.getSize();

  const sizeStyles = isStickyColumn
    ? {
        width: `${columnWidth}px`,
        minWidth: `${columnWidth}px`,
        maxWidth: `${columnWidth}px`,
      }
    : {
        minWidth: `${columnWidth}px`,
        flex: "1 1 auto",
      };

  if (isStickyColumn) {
    return {
      ...baseStyle,
      ...sizeStyles,
      position: "sticky" as const,
      ...(isActionsColumn ? { right: 0 } : { left: 0 }),
      backgroundColor: "white",
      zIndex: 3,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    };
  }

  return {
    ...baseStyle,
    ...sizeStyles,
    zIndex: 2,
    textAlign: "left",
  };
};

export const StyledTableRow = styled(TableRow)<{
  isSelected: boolean;
  isChecked: boolean;
}>`
  display: flex;
  cursor: pointer;
  background-color: ${props => {
    if (props.isSelected) {
      return rgbaToRgb(hexToRgba(Theme.colors.adskBlue300, 0.3));
    }
    if (props.isChecked) {
      return rgbaToRgb(hexToRgba(Theme.colors.adskBlue100, 0.3));
    }
    return "white";
  }};

  &:hover {
    background-color: ${props => {
      if (props.isSelected) {
        return Theme.colors.adskBlue100;
      }
      if (props.isChecked) {
        return Theme.colors.adskBlue100;
      }
      return Theme.colors.charcoal050;
    }};
  }
`;

export const FloatingPanel = styled.div`
  &:focus-within {
    position: relative;
    z-index: 2;
    background: white;
  }
`;

export const tableCellForRowStyle: React.CSSProperties = {
  borderBottom: `1px solid ${Theme.colors.charcoal100}`,
  backgroundColor: "inherit",
};
